// Ícono cuadrado de la marca (`locations.icon_url`), aparte del logo.
//
// El logo (`logo_url`) suele ser un wordmark apaisado y transparente pensado
// para ir SOBRE una foto; el ícono es la imagen 1:1 (la «E», el isotipo) que va
// donde la forma es fija: el círculo de la página de reseñas, el favicon y el
// ícono de la app del micrositio, los avatares del directorio.
//
// El panel lo sube por /api/upload/location-image (tipo `icon`): recortado al
// centro a 512×512, WebP sin metadatos, en `<location_id>/icon.webp`.
import { isMissingColumnError } from "@/lib/menu/ruleColumns"
import { storagePathFromPublicUrl } from "@/lib/storage/locationImageCleanup"

export const ICON_BUCKET = "location-images"

/**
 * Fragmento para interpolar al FINAL de la lista de columnas de `locations`
 * (con su coma adelante), o vacío para la consulta de siempre.
 */
export function iconColumn(withIcon: boolean): string {
  return withIcon ? ", icon_url" : ""
}

/**
 * Corre la consulta pidiendo `icon_url` y, si falla SOLO porque la columna no
 * existe (código desplegado antes de la migración 20260930021222), la repite
 * sin ella. Nombrar una columna inexistente tumba la consulta ENTERA con 42703:
 * la página de reseñas diría «link inválido» y el directorio no cargaría. Regla
 * 7 de docs/DEPLOY.md; mismo patrón que lib/menu/ruleColumns.ts.
 */
export async function selectWithIconColumn<R extends { error: unknown }>(
  run: (withIcon: boolean) => PromiseLike<R>,
): Promise<R> {
  const first = await run(true)
  if (first.error && isMissingColumnError(first.error)) return run(false)
  return first
}

function clean(url: string | null | undefined): string | null {
  const u = (url ?? "").trim()
  return u || null
}

/**
 * La imagen para un avatar de marca (cuadrado o círculo con `object-fit:
 * cover`): el ícono si hay; si no, el logo como hasta ahora.
 */
export function brandAvatarUrl(row: { icon_url?: string | null; logo_url?: string | null } | null | undefined): string | null {
  return clean(row?.icon_url) ?? clean(row?.logo_url)
}

/**
 * Ruta dentro del bucket si `url` es el ícono que subió ESTE local
 * (`<locationId>/icon.<ext>`), o null. Es lo único que se acepta como
 * `icon_url`: el favicon lo sirve el servidor descargando ese objeto, así que
 * nunca se va a buscar una URL arbitraria (ni a otro host ni a la carpeta de
 * otro local).
 */
export function iconStoragePath(locationId: string, url: string | null | undefined): string | null {
  const u = clean(url)
  if (!u || !locationId) return null
  const path = storagePathFromPublicUrl(u)
  if (!path) return null
  return /^[^/]+\/icon\.[a-z0-9]+$/i.test(path) && path.startsWith(`${locationId}/`) ? path : null
}

/**
 * Validación del valor que llega a `updateLocationConfig`: vacío/null (quitar
 * el ícono) o el ícono de este local. Devuelve el valor normalizado.
 */
export function validateIconUrl(
  locationId: string,
  value: unknown,
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (value === null || value === undefined) return { ok: true, value: null }
  if (typeof value !== "string") return { ok: false, error: "Ícono inválido" }
  if (!value.trim()) return { ok: true, value: null }
  if (!iconStoragePath(locationId, value)) return { ok: false, error: "Ícono inválido: súbelo desde Configuración → Negocio" }
  return { ok: true, value: value.trim() }
}
